import { Component } from '@theme/component';
import { BundleUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';
import { syncBundleSubscriptionCadence } from '@theme/bundle-subscription-cadence';

/**
 * Bundle / quantity-tier selector.
 *
 * Each tier is a `.bundle-option` carrying `data-bundle-quantity`. Selecting a
 * tier drives the product form's existing `quantity-selector-component` via its
 * public `setValue()` API and dispatches a `QuantitySelectorUpdateEvent` so the
 * sticky add-to-cart stays in sync.
 *
 * When the product has a color option, the panel shows one
 * `.bundle-colors__row` per unit for every tier (rows default to different
 * colors). Row 1 forwards its choice to the main picker's color circles so
 * gallery/price/URL stay in sync — without moving the other rows. Only a
 * DIRECT click on the picker's circles re-derives untouched rows (cycling
 * color offset).
 * On submit, `ProductFormComponent.handleSubmit` asks this component (via the
 * public `getBundleItems()`) for grouped line items and adds them in one
 * batch; for a single unit the regular single-variant submission runs.
 * Tier/color changes are broadcast as `BundleUpdateEvent`s so the sticky
 * add-to-cart bar can mirror the bundle summary and price.
 *
 * @typedef {object} BundleSelectorRefs
 * @property {HTMLElement} [colorPanel] - The per-unit color panel.
 * @property {HTMLElement[]} [colorRows] - One row per bundle unit.
 *
 * @extends {Component<BundleSelectorRefs>}
 */
class BundleSelectorComponent extends Component {
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #sealSyncTimer;
  #sealSelectionTimer;

  #subscriptionOriginals = new WeakMap();

  /**
   * True while row 1 forwards its swatch to the main picker. The picker's
   * `change` re-enters #onSectionChange, which would re-derive the OTHER
   * untouched rows — picking Fan I must never move Fan II/III.
   * @type {boolean}
   */
  #forwardingRowColor = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onActivate, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });

    const section = this.closest('.shopify-section');

    // Follow the main variant picker's color for rows the buyer hasn't touched.
    section?.addEventListener('change', this.#onSectionChange, { signal });
    // Seal's custom events are not emitted by every widget interaction/version.
    // Follow the actual ACV purchase-option click/change as well.
    section?.addEventListener('click', this.#onPurchaseOptionChange, { signal });
    section?.addEventListener('change', this.#onPurchaseOptionChange, { signal });

    for (const eventName of [
      'sealsubs:subscription_widget_created',
      'sealsubs:selling_plan_changed',
      'sealsubs:price_update',
    ]) {
      document.addEventListener(eventName, this.#onSealUpdate, { signal });
    }

    // Sync the quantity to the pre-selected tier once the quantity selector has upgraded.
    customElements.whenDefined('quantity-selector-component').then(() => {
      const selected = this.#selectedOption() ?? this.#firstOption();
      if (selected) this.#syncQuantity(selected);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.clearTimeout(this.#sealSyncTimer);
    window.clearTimeout(this.#sealSelectionTimer);
    this.#abortController.abort();
  }

  /** @param {Event} event */
  #onActivate = (event) => {
    if (!(event.target instanceof Element)) return;

    const swatch = /** @type {HTMLButtonElement | null} */ (event.target.closest('.bundle-colors__swatch'));
    if (swatch && this.contains(swatch)) {
      this.#selectSwatch(swatch);
      return;
    }

    const option = this.#optionFrom(event.target);
    if (option) this.#select(option);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    const option = this.#optionFrom(event.target);
    if (!option) return;
    event.preventDefault();
    this.#select(option);
  };

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #optionFrom(target) {
    if (!(target instanceof Element)) return null;
    const option = /** @type {HTMLElement | null} */ (target.closest('.bundle-option'));
    return option && this.contains(option) ? option : null;
  }

  /** @returns {HTMLElement | null} */
  #selectedOption() {
    return this.querySelector('.bundle-option[aria-checked="true"]');
  }

  /** @returns {HTMLElement | null} */
  #firstOption() {
    return this.querySelector('.bundle-option');
  }

  /** @param {HTMLElement} option */
  #select(option) {
    for (const el of this.querySelectorAll('.bundle-option')) {
      const selected = el === option;
      el.classList.toggle('bundle-option--selected', selected);
      el.setAttribute('aria-checked', selected ? 'true' : 'false');
    }
    this.#syncQuantity(option);
    this.#revealBuyButtons();
  }

  /**
   * After a user picks a tier on mobile, nudge the page just enough to bring
   * the Buy now / Add to cart buttons fully into view — but only when they're
   * currently clipped by the fold. Adding units grows the color panel and
   * pushes the buttons down, so measure AFTER #syncQuantity has reflowed.
   * No-op on desktop (that add flow leaves the page for /cart) and whenever
   * the buttons already sit inside the viewport.
   */
  #revealBuyButtons() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    // Older Instagram/Facebook Android WebViews can mis-handle the object
    // form of window.scrollBy and jump to the gallery when a tier is clicked.
    // Keep the selection in place there; regular mobile browsers retain the
    // gentle Add to cart reveal below.
    if (/Instagram|FBAN|FBAV/i.test(navigator.userAgent || '')) return;

    const scope = this.closest('.shopify-section') ?? document;
    // Scope to the main buy-buttons block: the `.add-to-cart-button` class is
    // also worn by the (hidden, zero-box) sticky bar and quick-add buttons in
    // the same section — a 0×0 rect would poison the region bounds. Keep only
    // laid-out elements.
    const buyBlock = scope.querySelector('.buy-buttons-block');
    if (!buyBlock) return;
    const rects = [buyBlock.querySelector('.buy-now-button'), buyBlock.querySelector('.add-to-cart-button')]
      .map((el) => el?.getBoundingClientRect())
      .filter(/** @returns {r is DOMRect} */ (r) => r != null && r.height > 0);
    if (!rects.length) return;
    const regionTop = Math.min(...rects.map((r) => r.top));
    const regionBottom = Math.max(...rects.map((r) => r.bottom));

    const margin = 16;
    const viewport = window.innerHeight;

    let delta = 0;
    if (regionBottom > viewport - margin) {
      // Clipped below the fold — scroll down just enough to clear the bottom,
      // but never so far that the region's top slips above the top edge.
      delta = Math.min(regionBottom - viewport + margin, Math.max(0, regionTop - margin));
    } else if (regionTop < margin) {
      // Clipped above — scroll up so the region's top clears the top edge.
      delta = regionTop - margin;
    }

    if (Math.abs(delta) < 1) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollBy({ top: delta, left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** @param {HTMLElement} option */
  #syncQuantity(option) {
    const quantity = parseInt(option.dataset.cartQuantity || option.dataset.bundleQuantity || '1', 10) || 1;
    const scope = this.closest('.shopify-section') ?? document;
    if (option.dataset.bundleVariantId) {
      for (const input of scope.querySelectorAll('form[action*="/cart/add"] input[name="id"]')) {
        input.value = option.dataset.bundleVariantId;
      }
    }
    const quantitySelector = /** @type {any} */ (scope.querySelector('quantity-selector-component'));
    if (quantitySelector?.setValue) {
      quantitySelector.setValue(String(quantity));
      quantitySelector.dispatchEvent(new QuantitySelectorUpdateEvent(quantity));
      const quantityInput = quantitySelector.querySelector('input[name="quantity"]');
      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    this.#syncRows(quantity);
    this.#syncAddToCartLabel(option);
    this.#scheduleSealSync(option);
  }

  /**
   * Mirrors the selected one-time quantity total in the main purchase button.
   * The base label comes from Shopify's translated Add to cart text.
   * @param {HTMLElement} option
   */
  #syncAddToCartLabel(option, displayedTotal = option.dataset.tierTotal) {
    const scope = this.closest('.shopify-section') ?? document;
    const button = /** @type {HTMLButtonElement | null} */ (
      scope.querySelector('.buy-buttons-block .add-to-cart-button')
    );
    const label = /** @type {HTMLElement | null} */ (button?.querySelector('[data-add-to-cart-label]') ?? null);
    if (!button || button.disabled || !label) return;

    const baseLabel = label.dataset.baseLabel || label.textContent?.trim();
    if (!baseLabel) return;

    label.dataset.baseLabel = baseLabel;
    label.textContent = displayedTotal ? `${baseLabel} · ${displayedTotal}` : baseLabel;
  }

  /** @param {CustomEvent} event */
  #onSealUpdate = (event) => {
    const scope = this.closest('.shopify-section') ?? document;
    if (event.detail?.element instanceof Element && !scope.contains(event.detail.element)) return;
    const selected = this.#selectedOption() ?? this.#firstOption();
    if (selected) this.#scheduleSealSync(selected);
  };

  #onPurchaseOptionChange = (event) => {
    if ((!this.classList.contains('bundle-selector--acv') && !this.querySelector('[data-fixed-bundle-pricing]')) ||
        !(event.target instanceof Element) ||
        !event.target.closest('.sealsubs-target-element')) return;
    const sync = () => {
      const selected = this.#selectedOption() ?? this.#firstOption();
      if (selected && this.isConnected) this.#scheduleSealSync(selected);
    };
    sync();
    // Re-read after Seal finishes updating its hidden selling_plan input.
    window.clearTimeout(this.#sealSelectionTimer);
    this.#sealSelectionTimer = window.setTimeout(sync, 150);
  };

  /** @param {HTMLElement} option */
  #scheduleSealSync(option) {
    window.clearTimeout(this.#sealSyncTimer);
    this.#sealSyncTimer = window.setTimeout(() => {
      syncBundleSubscriptionCadence(this.closest('.shopify-section') ?? document, option);
      this.#syncSealPrices(option);
    }, 0);
  }

  // Also run synchronously at submission, including a quick tier → Buy now click.
  syncSubscriptionCadence() {
    syncBundleSubscriptionCadence(this.closest('.shopify-section') ?? document, this.#selectedOption());
  }

  /**
   * Seal exposes the selected selling plan but intentionally leaves quantity
   * control to the theme. Mirror this selector's bundle total into Seal's
   * visible price rows without changing its plan input or checkout behavior.
   * @param {HTMLElement} option
   */
  #syncFixedPackPrices(option) {
    const scope = this.closest('.shopify-section') ?? document;
    const form = scope.querySelector('form[action*="/cart/add"]');
    const data = this.querySelector('[data-fixed-bundle-pricing]');
    if (!form || !data) return;
    const pricing = JSON.parse(data.textContent);
    const planId = scope.querySelector('input.sls-option[data-type="one_time"]:checked')
      ? '' : form.querySelector('[name="selling_plan"]')?.value || '';
    const variants = pricing.variants;
    for (const tier of this.querySelectorAll('.bundle-option')) {
      const variant = variants.find(item => String(item.id) === tier.dataset.bundleVariantId);
      if (!variant) continue;
      const allocation = variant.allocations.find(item => String(item.selling_plan_id) === planId);
      const cents = allocation?.price ?? variant.price;
      const priceColumn = tier.querySelector('.bundle-option__pricecol');
      if (priceColumn) {
        if (!this.#subscriptionOriginals.has(tier)) this.#subscriptionOriginals.set(tier, {html: priceColumn.innerHTML});
        priceColumn.innerHTML = this.#subscriptionOriginals.get(tier).html;
      }
      tier.dataset.tierTotal = this.#formatPriceLike(tier.dataset.tierTotal, cents);
      const column = tier.querySelector('.bundle-option__price');
      if (column) column.textContent = tier.dataset.tierTotal;
      const each = tier.querySelector('[data-bundle-each]');
      if (each) each.textContent = this.#formatPriceLike(tier.dataset.tierTotal, Math.round(cents / Number(tier.dataset.bundleQuantity))) + ' / each';
    }
    const widget = scope.querySelector('.sealsubs-target-element.sealsubs-full[data-product]');
    const variant = variants.find(item => String(item.id) === option.dataset.bundleVariantId);
    if (widget && variant) {
      const widgetPlanId = widget.querySelector('select[name="subs_interval"]')?.value || planId;
      const allocation = variant.allocations.find(item => String(item.selling_plan_id) === widgetPlanId) ?? variant.allocations[0];
      for (const [type, cents] of [['one_time', variant.price], ['subscription', allocation?.price ?? variant.price]]) {
        const container = widget.querySelector('input.sls-option[data-type="' + type + '"]')?.closest('.sls-option-container');
        const price = container?.querySelector('.sls-price > .money') ?? [...(container?.querySelectorAll('.money') ?? [])].at(-1);
        if (price) price.textContent = this.#formatPriceLike(price.textContent, cents);
        const original = container?.querySelector('.sls-original-price .money');
        if (original) original.textContent = this.#formatPriceLike(original.textContent, Number(option.dataset.tierCompareCents));
      }
    }
    this.#syncAddToCartLabel(option);
    this.#notifyBundleUpdate();
  }

  #syncSealPrices(option) {
    if (option.dataset.bundleVariantId) {
      this.#syncFixedPackPrices(option);
      return;
    }
    const scope = this.closest('.shopify-section') ?? document;
    const widget = /** @type {HTMLElement | null} */ (
      scope.querySelector('.sealsubs-target-element.sealsubs-full[data-product]')
    );
    const form = scope.querySelector('form[action*="/cart/add"]');
    const quantity = Number(option.dataset.bundleQuantity) || 1;
    const oneTimeCents = Number(option.dataset.tierTotalCents);
    const compareCents = Number(option.dataset.tierCompareCents) || oneTimeCents;
    if (!widget || !form || !Number.isFinite(oneTimeCents)) return;

    let productData;
    try {
      productData = JSON.parse(widget.dataset.product || '{}');
    } catch (_error) {
      return;
    }

    const variantId = form.querySelector('[name="id"]')?.value;
    const variant =
      productData.variants?.find((item) => String(item.id) === String(variantId)) ?? productData.variants?.[0];
    const planId = form.querySelector('[name="selling_plan"]')?.value || '';
    const allocation = variant?.selling_plan_allocations?.find(
      (item) => String(item.selling_plan_id) === String(planId)
    );
    const widgetPlanId = widget.querySelector('select[name="subs_interval"]')?.value || '';
    const subscriptionAllocation =
      allocation ??
      variant?.selling_plan_allocations?.find((item) => String(item.selling_plan_id) === String(widgetPlanId)) ??
      variant?.selling_plan_allocations?.[0];
    let subscriptionCents = subscriptionAllocation
      ? Number(subscriptionAllocation.price) * quantity
      : oneTimeCents;
    const subscriptionCompareCents = variant?.price ? Number(variant.price) * quantity : compareCents;

    // Match Shopify's percentage bundle rules after the selling-plan price.
    // Shopify floors the discount to whole cents per unit.
    if (this.classList.contains('bundle-selector--acv') && variant && subscriptionAllocation) {
      const subscriptionRate = Number(subscriptionAllocation.price) / Number(variant.price);
      const totalFor = (tier) => {
        const qty = Number(tier.dataset.bundleQuantity);
        const bundlePercent = Math.round((1 - Number(tier.dataset.tierTotalCents) / qty / Number(variant.price)) * 100);
        const planPrice = Number(subscriptionAllocation.price);
        return (planPrice - Math.floor(planPrice * bundlePercent / 100)) * qty;
      };
      subscriptionCents = totalFor(option);
      for (const tier of this.querySelectorAll('.bundle-option')) {
        const column = tier.querySelector('.bundle-option__pricecol');
        if (!column) continue;
        if (!this.#subscriptionOriginals.has(tier)) this.#subscriptionOriginals.set(tier, {
          html: column.innerHTML, total: tier.dataset.tierTotal, was: tier.dataset.tierWas,
        });
        const original = this.#subscriptionOriginals.get(tier);
        column.innerHTML = original.html;
        tier.dataset.tierTotal = original.total;
        if (original.was) tier.dataset.tierWas = original.was;
        else delete tier.dataset.tierWas;
        const body = tier.querySelector('.bundle-option__body');
        const bundlePercent = Math.round((1 - Number(tier.dataset.tierTotalCents) / Number(tier.dataset.bundleQuantity) / Number(variant.price)) * 100);
        const subscriptionPercent = allocation ? Math.round((1 - subscriptionRate) * 100) : 0;
        let visibleBadge = body?.querySelector('.acv-bundle-saving');
        if (body && (bundlePercent > 0 || subscriptionPercent > 0)) {
          if (!visibleBadge) {
            visibleBadge = document.createElement('span');
            visibleBadge.className = 'acv-bundle-saving';
            body.append(visibleBadge);
          }
          visibleBadge.textContent = bundlePercent > 0
            ? `Save ${bundlePercent}%${subscriptionPercent > 0 ? ` + extra ${subscriptionPercent}%` : ''}`
            : `Save ${subscriptionPercent}%`;
        } else visibleBadge?.remove();
        if (!allocation) continue;
        const qty = Number(tier.dataset.bundleQuantity);
        const savings = Math.round((1 - subscriptionRate) * 100);
        const sticker = column.querySelector('.bundle-option__sticker-pct');
        if (sticker) {
          sticker.textContent += ` + extra ${savings}%`;
          const row = column.querySelector('.bundle-option__wasrow');
          row.style.flexDirection = 'column';
          row.style.alignItems = 'flex-end';
        }
        else {
          const badge = document.createElement('span');
          badge.className = 'bundle-option__sticker';
          badge.textContent = `Save ${savings}%`;
          column.append(badge);
        }
        tier.dataset.tierTotal = this.#formatPriceLike(original.total, totalFor(tier));
        if (qty > 1) tier.dataset.tierWas = this.#formatPriceLike(original.total, Number(variant.price) * qty);
        else delete tier.dataset.tierWas;
        column.querySelector('.bundle-option__price').textContent = tier.dataset.tierTotal;
      }
      this.#notifyBundleUpdate();
    }

    const updateOption = (type, currentCents, originalCents) => {
      const input = widget.querySelector(`input.sls-option[data-type="${type}"]`);
      const container = input?.closest('.sls-option-container');
      if (!container) return;
      const current = container.querySelector('.sls-price > .money') ?? [...container.querySelectorAll('.money')].at(-1);
      const original = container.querySelector('.sls-original-price .money');
      if (current) current.textContent = this.#formatPriceLike(current.textContent, currentCents);
      if (original) original.textContent = this.#formatPriceLike(original.textContent, originalCents);
    };

    updateOption('subscription', subscriptionCents, subscriptionCompareCents);
    updateOption('one_time', oneTimeCents, compareCents);

    const displayedTotal = allocation
      ? this.#formatPriceLike(option.dataset.tierTotal || '', subscriptionCents)
      : option.dataset.tierTotal;
    this.#syncAddToCartLabel(option, displayedTotal);
  }

  /** @param {string | null | undefined} sample @param {number} cents */
  #formatPriceLike(sample, cents) {
    const source = sample || '$0.00';
    const firstDigit = source.search(/\d/);
    const lastDigit = Math.max(...[...source].map((char, index) => (/\d/.test(char) ? index : -1)));
    if (firstDigit < 0 || lastDigit < firstDigit) return (cents / 100).toFixed(2);
    const numericSample = source.slice(firstDigit, lastDigit + 1);
    const decimalSeparator = numericSample.lastIndexOf(',') > numericSample.lastIndexOf('.') ? ',' : '.';
    const separatorIndex = numericSample.lastIndexOf(decimalSeparator);
    const precision = separatorIndex >= 0 ? numericSample.length - separatorIndex - 1 : 2;
    const amount = (cents / 100).toFixed(precision).replace('.', decimalSeparator);
    return `${source.slice(0, firstDigit)}${amount}${source.slice(lastDigit + 1)}`;
  }

  /**
   * Shows one color row per bundle unit (the panel is always visible; a single
   * unit shows one row, kept in sync with the main picker's circles).
   * @param {number} quantity
   */
  #syncRows(quantity) {
    const { colorPanel, colorRows } = this.refs;
    if (!colorPanel || !colorRows?.length) {
      this.#notifyBundleUpdate();
      return;
    }

    colorRows.forEach((row, index) => {
      row.hidden = index >= quantity;
    });
    this.#notifyBundleUpdate();
  }

  /** @param {HTMLButtonElement} swatch */
  #selectSwatch(swatch) {
    const row = swatch.closest('.bundle-colors__row');
    if (!(row instanceof HTMLElement)) return;

    row.dataset.touched = 'true';
    for (const el of row.querySelectorAll('.bundle-colors__swatch')) {
      el.setAttribute('aria-checked', el === swatch ? 'true' : 'false');
    }

    // The first unit doubles as the page-level color: forward it to the hidden
    // main variant picker so the gallery, price, and availability stay in sync.
    if (row === this.refs.colorRows?.[0]) {
      this.#syncMainPicker(swatch.dataset.colorName);
    }

    this.#notifyBundleUpdate();
  }

  /**
   * Checks the main variant picker's radio for the given color, if not already.
   * @param {string | undefined} colorName
   */
  #syncMainPicker(colorName) {
    const optionName = this.refs.colorPanel?.dataset.optionName;
    if (!optionName || !colorName) return;

    const scope = this.closest('.shopify-section') ?? document;
    for (const input of scope.querySelectorAll('variant-picker input[type="radio"]')) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.optionName === optionName &&
        input.value === colorName
      ) {
        if (!input.checked) {
          // click() fires `change` synchronously — flag it so #onSectionChange
          // leaves the other rows alone (gallery/price still update normally).
          this.#forwardingRowColor = true;
          try {
            input.click();
          } finally {
            this.#forwardingRowColor = false;
          }
        }
        return;
      }
    }
  }

  /**
   * Keeps untouched rows in sync when the buyer changes color in the main
   * variant picker. Rows keep the "different colors by default" pattern: row n
   * follows the picker's color offset by n-1 steps (cycling), so a 2-pack
   * stays e.g. Green + Black after switching the picker to Green.
   * @param {Event} event
   */
  #onSectionChange = (event) => {
    // Change originated from row 1's forwarded swatch click — the other rows
    // must not be re-derived (picking Fan I must never move Fan II/III).
    if (this.#forwardingRowColor) return;

    const { colorPanel, colorRows } = this.refs;
    const input = event.target;
    if (!colorPanel || !colorRows?.length || !(input instanceof HTMLInputElement)) return;
    if (!input.closest('variant-picker') || input.dataset.optionName !== colorPanel.dataset.optionName) return;

    colorRows.forEach((row, rowIndex) => {
      if (row.dataset.touched === 'true') return;
      const swatches = /** @type {HTMLElement[]} */ ([...row.querySelectorAll('.bundle-colors__swatch')]);
      const baseIndex = swatches.findIndex((el) => el.dataset.colorName === input.value);
      if (baseIndex === -1) return;

      let targetIndex = (baseIndex + rowIndex) % swatches.length;
      if (swatches[targetIndex]?.hasAttribute('disabled')) targetIndex = baseIndex;

      swatches.forEach((el, i) => {
        el.setAttribute('aria-checked', i === targetIndex && !el.hasAttribute('disabled') ? 'true' : 'false');
      });
    });
    this.#notifyBundleUpdate();
  };

  /**
   * Collects the per-unit selections as cart line items, or null when the
   * regular single-variant submission should run instead (single unit: row 1
   * already drives the main picker, so the default form flow is correct).
   * Called by `ProductFormComponent.handleSubmit` before its default submission.
   * @returns {Array<{variantId: string, quantity: number}> | null}
   */
  getBundleItems() {
    const rows = this.#visibleRows();
    if (rows.length < 2) return null;

    /** @type {Map<string, number>} */
    const counts = new Map();
    for (const row of rows) {
      const checked = /** @type {HTMLElement | null} */ (row.querySelector('.bundle-colors__swatch[aria-checked="true"]'));
      const variantId = checked?.dataset.variantId;
      if (!variantId) return null;
      counts.set(variantId, (counts.get(variantId) ?? 0) + 1);
    }
    if (counts.size === 0) return null;

    return Array.from(counts, ([variantId, quantity]) => ({ variantId, quantity }));
  }

  /** @returns {HTMLElement[]} */
  #visibleRows() {
    return (this.refs.colorRows ?? []).filter((row) => !row.hidden);
  }

  /**
   * Current bundle display state for the sticky add-to-cart bar. Also pulled
   * directly by sticky-add-to-cart.js on connect, in case it upgrades after
   * the initial BundleUpdateEvent was dispatched.
   * @returns {{quantity: number, summary: string | null, price: string | null, compare: string | null}}
   */
  getStickyDisplay() {
    const selected = this.#selectedOption();
    const quantity = parseInt(selected?.dataset.bundleQuantity || '1', 10) || 1;

    /** @type {string | null} */
    let summary = null;
    /** @type {string | null} */
    let price = null;
    /** @type {string | null} */
    let compare = null;

    if (quantity >= 2 || this.classList.contains('bundle-selector--acv')) {
      price = selected?.dataset.tierTotal ?? null;
      compare = selected?.dataset.tierWas ?? null;

      /** @type {Map<string, number>} */
      const counts = new Map();
      for (const row of this.#visibleRows()) {
        const checked = /** @type {HTMLElement | null} */ (row.querySelector('.bundle-colors__swatch[aria-checked="true"]'));
        const name = checked?.dataset.colorName;
        if (!name) {
          counts.clear();
          break;
        }
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
      if (counts.size > 0) {
        summary = Array.from(counts, ([name, count]) => `${count}× ${name}`).join(' / ');
      }
    }

    return { quantity, summary, price, compare };
  }

  #notifyBundleUpdate() {
    document.dispatchEvent(new BundleUpdateEvent(this.getStickyDisplay()));
  }

}

if (!customElements.get('bundle-selector-component')) {
  customElements.define('bundle-selector-component', BundleSelectorComponent);
}

